import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ResetListState from '../../../components/ResetListState';
import { BatchConstants } from 'lookup/BatchConstants';
import { MdClose } from "react-icons/md";
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { updateBatchList, resetError, updateSwitchList } from './BatchList.actions';
import { formatDateWithTime } from 'services/MomentUtils';
import { twoDecimalPlaceRounder } from 'services/UtilityService';
import { WrapTextAndCenterAlign, WrapTextAndJustifyCenter } from 'components/WrapText';
import './BatchList.css';
import { showNotification } from 'services/NotificationService';
import ServerSideReactTable from 'commons/table/ServerSideReactTable';
import { FilterTypes, ColumnTypes } from 'commons/table/Constants';
import EditBatch from './EditBatch';
import ReactBadge from "react-bootstrap/Badge";

const Badge = (props) => (
  <div>
    <ReactBadge {...props}>{props.children}</ReactBadge>
  </div>
);

const DEFAULT_PAGE_SIZE = 15;

const BatchList = () => {
  const [selectedBatchId, setSelectedBatchId] = useState(-1);
  const [displayAllColumn, setDisplayAllColumn] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data = [],
    pages = 0,
    switchPages = 0,
    switchListTotalElements = "Loading ...",
    switchData = [],
    totalElements = "Loading ...",
    loading,
    listState: initialListState,
    switchListState: initialSwitchListState,
    switchLoading,
    isError,
  } = useSelector(state => state.batchList);

  const listState = initialListState || {
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sorted: BatchConstants.DEFAULT_BATCH_LIST_SORTING,
    filtered: BatchConstants.DEFAULT_BATCH_LIST_FILTERS,
    resized: [],
    expanded: {}
  };

  const switchListState = initialSwitchListState || {
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sorted: [],
    filtered: [],
    resized: [],
    expanded: {}
  };

  const updateData = useCallback((state) => {
    if (state.sorted !== BatchConstants.DEFAULT_BATCH_LIST_SORTING) {
      state.sorted.forEach((element) => {
        element.isDefault = false;
      });
    }
    const filteredForStatus = state.filtered.filter(element => !(element.id === 'status' && element.value.includes("ALL_FILTERS")));
    if (filteredForStatus.length !== state.filtered.length) {
      state.filtered = filteredForStatus;
    }
    dispatch(updateBatchList(state.pageSize, state.page, state.sorted, state.filtered, state.resized, state.expanded));
  }, [dispatch]);

  useEffect(() => {
    updateData(listState);
  }, []);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: 'Error Loading Data!',
        message: 'Unable to load data.',
        position: 'bl',
        type: 'error'
      });
      dispatch(resetError());
    }
  }, [isError, dispatch]);

  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    const newListState = {
      ...listState,
      pageSize,
      page: pageNo,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
    };
    updateData(newListState);
  };

  const updateSwitchData = useCallback((state, preventUpdate, batchId) => {
    if (!preventUpdate && batchId !== -1) {
      dispatch(updateSwitchList(batchId, state.pageSize, state.page, state.sorted, state.filtered, state.resized, state.expanded));
    }
  }, [dispatch]);

  const onUpdateSwitchTable = (filterValues, sortBy, pageSize, pageNo) => {
    const newSwitchListState = {
      ...switchListState,
      pageSize,
      page: pageNo,
      sorted: sortBy ? sortBy : [],
      filtered: filterValues,
    };
    updateSwitchData(newSwitchListState, false, selectedBatchId);
  };

  const handler = useCallback(() => {
    updateData(listState, false);
  }, [listState, updateData]);

  const renderActions = (rowData) => (
    <span className="action-button">
      <EditBatch
        batch={rowData}
        showButton={true}
        handler={handler}
      />
    </span>
  );

  const renderRowDescription = (rowData) => (
    <span style={{ display: "flex" }}>
      <span style={{ flex: "9" }}>
        {rowData.description}
      </span>
      <span className="edited-tag">
        {rowData.descriptionEdited && (
          <Badge
            variant="primary"
            box="true"
            className="badge-custom-edited"
          >
            Edited
          </Badge>
        )}
      </span>
    </span>
  );

  const renderDateCell = (date) => (
    <span>
      {formatDateWithTime(date)}
    </span>
  );

  const renderSavingsCell = (savings) => `$ ${twoDecimalPlaceRounder(savings)}`;

  const widthOfTotalSwitch = displayAllColumn ? 95 : 100;
  const widthOfSwitchDescription = displayAllColumn ? 110 : 115;

  const columns = [
    {
      header: "Description",
      accessor: "description",
      accessorKey: "description",
      enableSorting: false,
      width: widthOfSwitchDescription,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      filterType: FilterTypes.SearchableInput,
      customComponent: (value, row) => renderRowDescription(row.original),
    },
    {
      header: "Batch Status",
      accessor: "status",
      accessorKey: "status",
      width: 115,
      show: displayAllColumn,
      enableSorting: true,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      filterType: FilterTypes.SingleSelect,
      filterOptions: BatchConstants.statuses,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Scheduled/Execution Date",
      accessor: "dateScheduled",
      accessorKey: "dateScheduled",
      enableSorting: true,
      width: 210,
      show: displayAllColumn,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => renderDateCell(value),
    },
    {
      header: "Total Switches",
      accessor: "numberOfSwitch",
      accessorKey: "numberOfSwitch",
      enableSorting: false,
      width: widthOfTotalSwitch,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Alternate Activity Creation",
      accessor: "isEvenSwitchActivityCreationEnabled",
      accessorKey: "isEvenSwitchActivityCreationEnabled",
      enableSorting: false,
      width: widthOfTotalSwitch,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value ? "Enabled" : "Disabled"}</WrapTextAndCenterAlign>,
    },
    {
      header: "Expired Switches",
      accessor: "numberOfExpiredSwitch",
      accessorKey: "numberOfExpiredSwitch",
      enableSorting: false,
      enableColumnFilter: false,
      width: 130,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Succesfully Delivered",
      accessor: "numberOfSuccessfullyDeliveredSwitch",
      accessorKey: "numberOfSuccessfullyDeliveredSwitch",
      enableSorting: false,
      enableColumnFilter: false,
      width: 150,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Failed Delivery",
      accessor: "numberOfFailedSwitch",
      accessorKey: "numberOfFailedSwitch",
      enableSorting: false,
      enableColumnFilter: false,
      width: 120,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Error",
      accessor: "numberOfErrorInDelivery",
      accessorKey: "numberOfErrorInDelivery",
      enableSorting: false,
      enableColumnFilter: false,
      width: 50,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Responses Recieved",
      accessor: "numberOfSwitchesWithResponse",
      accessorKey: "numberOfSwitchesWithResponse",
      enableSorting: false,
      enableColumnFilter: false,
      width: 140,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Declined State",
      accessor: "numberOfDeclined",
      accessorKey: "numberOfDeclined",
      enableSorting: false,
      enableColumnFilter: false,
      width: 140,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Closed State",
      accessor: "numberOfClosed",
      accessorKey: "numberOfClosed",
      enableSorting: false,
      enableColumnFilter: false,
      width: 100,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Fill Pending State",
      accessor: "numberOfFillPending",
      accessorKey: "numberOfFillPending",
      enableSorting: false,
      enableColumnFilter: false,
      width: 120,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Others",
      accessor: "others",
      accessorKey: "others",
      enableSorting: false,
      enableColumnFilter: false,
      width: 55,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Projected Savings",
      accessor: "projectedSavings",
      accessorKey: "projectedSavings",
      enableSorting: false,
      enableColumnFilter: false,
      width: 120,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Completed Date/Time",
      accessor: "completionDate",
      accessorKey: "completionDate",
      enableSorting: false,
      width: 210,
      show: displayAllColumn,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => row.original.status === "CLOSED" ? renderDateCell(value) : <WrapTextAndJustifyCenter>N/A</WrapTextAndJustifyCenter>,
    },
    {
      header: "Action",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableFiltering: false,
      show: displayAllColumn,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => renderActions(row.original),
    },
  ];

  const switchColumns = [
    {
      header: "Switch Id",
      accessor: "switchId",
      accessorKey: "switchId",
      enableSorting: false,
      enableColumnFilter: false,
      width: 95,
      show: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Cust",
      accessor: "customerNameAbbreviation",
      accessorKey: "customerNameAbbreviation",
      enableSorting: false,
      enableColumnFilter: false,
      width: 60,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Client",
      accessor: "clientNameAbbreviation",
      accessorKey: "clientNameAbbreviation",
      enableSorting: false,
      enableColumnFilter: false,
      width: 60,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Switch Id",
      accessor: "switchNumber",
      accessorKey: "switchNumber",
      enableSorting: true,
      enableColumnFilter: false,
      width: 60,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "# of Messages Sent",
      accessor: "numberOfTimeFaxSent",
      accessorKey: "numberOfTimeFaxSent",
      enableSorting: false,
      enableColumnFilter: false,
      width: 150,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "# of Messages Delivered",
      accessor: "numberOfDeliveredFax",
      accessorKey: "numberOfDeliveredFax",
      enableSorting: false,
      enableColumnFilter: false,
      width: 160,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Current Status",
      accessor: "currentStatus",
      accessorKey: "currentStatus",
      enableSorting: true,
      enableColumnFilter: false,
      width: 150,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value}</WrapTextAndCenterAlign>,
    },
    {
      header: "Scheduled Date",
      accessor: "scheduledDispatchTime",
      accessorKey: "scheduledDispatchTime",
      enableSorting: true,
      enableColumnFilter: false,
      width: 150,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value ? renderDateCell(value) : 'N/A'}</WrapTextAndCenterAlign>,
    },
    {
      header: "Fax Status",
      accessor: "faxBucketStatus",
      accessorKey: "faxBucketStatus",
      enableSorting: true,
      enableColumnFilter: false,
      width: 150,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => <WrapTextAndCenterAlign>{value ? value : "N/A"}</WrapTextAndCenterAlign>,
    },
    {
      header: "Projected Savings",
      accessor: "projectedSavings",
      accessorKey: "projectedSavings",
      enableSorting: false,
      enableColumnFilter: false,
      width: 140,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => renderSavingsCell(value),
    },
  ];

  const processedSwitchData = switchData.map(element => ({ ...element, lastCycleId: element.lastCycleId || '-' }));

  return (
    <Container className="list-container" fluid>
      <Row >
        <Col md={12}>
          <div className='codex-version-display-bar'>
            <span style={{ float: 'left' }} >   Total Batches: <strong>{totalElements}</strong></span>
            <strong style={{ float: 'center' }}>Batch List</strong>
            {selectedBatchId === -1 && (
              <ResetListState
                onReset={() => updateData({
                  ...listState,
                  sorted: BatchConstants.DEFAULT_BATCH_LIST_SORTING,
                  filtered: BatchConstants.DEFAULT_BATCH_LIST_FILTERS
                })}
                resetterDisabled={
                  listState.sorted === BatchConstants.DEFAULT_BATCH_LIST_SORTING &&
                  listState.filtered === BatchConstants.DEFAULT_BATCH_LIST_FILTERS
                }
              />
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={selectedBatchId === -1 ? 12 : 4}>
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            totalPages={pages}
            totalRecords={totalElements === "loading" ? 0 : totalElements}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            onUpdateTableData={onUpdateTable}
            trStyle={(rowInfo) => ({
              background: (rowInfo && rowInfo.original.wfBatchId === selectedBatchId) ? "#42a5f5" : "",
              color: (rowInfo && rowInfo.original.wfBatchId === selectedBatchId) ? "#ffffff" : ""
            })}
            onRowClick={(rowInfo) => {
              if (rowInfo?.original.wfBatchId !== undefined && rowInfo.column.id !== 'action') {
                if (rowInfo.original.wfBatchId !== -1) {
                  const newSwitchListState = { ...switchListState, page: 0 };
                  updateSwitchData(newSwitchListState, false, rowInfo.original.wfBatchId);
                }
                setSelectedBatchId(rowInfo.original.wfBatchId);
                setDisplayAllColumn(false);
              }
            }}
          />
        </Col>
        {selectedBatchId !== -1 && (
          <Col md={8} className="tiny-top-spacer">
            <Row >
              <Col md={12}>
                <Button className={"pull-right"} size="sm" variant="custom-hollow" onClick={() => {
                  setSelectedBatchId(-1);
                  setDisplayAllColumn(true);
                }}>
                  <MdClose />
                </Button>
                <h4 className="pull-left">
                  Switches for Batch
                </h4>
              </Col>
            </Row>
            <Row className="tiny-top-spacer">
              <Col md={12}>
                <ServerSideReactTable
                  currentPage={switchListState.page}
                  sorted={switchListState.sorted}
                  filters={switchListState.filtered}
                  totalPages={switchPages}
                  totalRecords={switchListTotalElements === "loading" ? 0 : switchListTotalElements}
                  enableMultiRowSelection={false}
                  addRowSelection={false}
                  columnDefination={switchColumns}
                  data={processedSwitchData}
                  loading={switchLoading}
                  defaultPageSize={switchListState.pageSize || DEFAULT_PAGE_SIZE}
                  pageSizeOptions={[5, 10, 15, 20, 30, 50]}
                  onUpdateTableData={onUpdateSwitchTable}
                  onRowClick={(rowInfo) => {
                    if (rowInfo) {
                      navigate(`/switch/${rowInfo.original.wfItemId}`);
                    }
                  }}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default BatchList;
